import React, { useState } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, Switch,
  FormControlLabel, Divider, Card, CardContent, Tabs, Tab,
  FormControl, InputLabel, Select, MenuItem, Snackbar, Alert,
  List, ListItem, ListItemText, ListItemSecondaryAction, Chip,
  TableContainer
} from '@mui/material';
import { Settings, School, Notifications, Storage, Save } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

const SettingsAdmin = () => {
  const location = useLocation();
  const getInitialTab = () => {
    if (location.pathname.includes('/monitoring')) return 2;
    if (location.pathname.includes('/data')) return 3;
    return 0;
  };

  const [tab, setTab] = useState(getInitialTab());
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const [schoolInfo, setSchoolInfo] = useState({
    name: 'Eagles Refuge Academy',
    tagline: 'Faith • Excellence • Character',
    address: '1234 Academy Drive',
    city: 'Austin',
    state: 'TX',
    zip: '78701',
    phone: '(512) 555-0100',
    email: 'info@eaglesrefugeacademy.edu',
    website: 'www.eaglesrefugeacademy.edu',
    principal: 'Dr. James Johnson',
    denomination: 'Non-Denominational Christian',
  });

  const [academicSettings, setAcademicSettings] = useState({
    currentYear: '2023-24',
    currentTerm: 'Spring 2024',
    gradingScale: '90-100: A, 80-89: B, 70-79: C, 60-69: D',
    gpaScale: '4.0',
    attendanceThreshold: '90',
    honorRollGpa: '3.5',
    probationGpa: '2.0',
  });

  const [notifPrefs, setNotifPrefs] = useState({
    newApplication: true,
    attendanceAlert: true,
    gradeReport: true,
    paymentDue: true,
    systemAlerts: true,
    weeklyDigest: false,
  });

  const handleSave = () => setSnackbar({ open: true, message: 'Settings saved successfully' });

  const systemInfo = [
    { label: 'Application Version', value: '1.0.0' },
    { label: 'Database Status', value: 'Connected', chip: true, color: 'success' },
    { label: 'Last Backup', value: '2024-01-18 02:00 AM', chip: false },
    { label: 'Total Records', value: '1,248', chip: false },
    { label: 'Active Sessions', value: '3', chip: false },
    { label: 'Server Status', value: 'Online', chip: true, color: 'success' },
  ];

  const dataLogs = [
    { action: 'Database Backup', status: 'success', time: '2024-01-18 02:00 AM', size: '124 MB' },
    { action: 'Database Backup', status: 'success', time: '2024-01-17 02:00 AM', size: '123 MB' },
    { action: 'Data Import', status: 'success', time: '2024-01-15 10:30 AM', size: '2.4 MB' },
    { action: 'Database Backup', status: 'success', time: '2024-01-16 02:00 AM', size: '122 MB' },
    { action: 'Data Export', status: 'success', time: '2024-01-14 3:45 PM', size: '8.1 MB' },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>System Settings</Typography>
        <Typography variant="body2" color="text.secondary">Manage school information, academic settings, and system preferences</Typography>
      </Box>

      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab icon={<School />} label="School Information" iconPosition="start" />
            <Tab icon={<Settings />} label="Academic Settings" iconPosition="start" />
            <Tab icon={<Notifications />} label="Notifications" iconPosition="start" />
            <Tab icon={<Storage />} label="System & Data" iconPosition="start" />
          </Tabs>
        </Box>

        {/* School Information */}
        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>School Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}><TextField label="School Name" fullWidth value={schoolInfo.name} onChange={e => setSchoolInfo(p => ({ ...p, name: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Tagline / Motto" fullWidth value={schoolInfo.tagline} onChange={e => setSchoolInfo(p => ({ ...p, tagline: e.target.value }))} /></Grid>
              <Grid item xs={12}><TextField label="Street Address" fullWidth value={schoolInfo.address} onChange={e => setSchoolInfo(p => ({ ...p, address: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={5}><TextField label="City" fullWidth value={schoolInfo.city} onChange={e => setSchoolInfo(p => ({ ...p, city: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={3}><TextField label="State" fullWidth value={schoolInfo.state} onChange={e => setSchoolInfo(p => ({ ...p, state: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={4}><TextField label="ZIP Code" fullWidth value={schoolInfo.zip} onChange={e => setSchoolInfo(p => ({ ...p, zip: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Main Phone" fullWidth value={schoolInfo.phone} onChange={e => setSchoolInfo(p => ({ ...p, phone: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Main Email" type="email" fullWidth value={schoolInfo.email} onChange={e => setSchoolInfo(p => ({ ...p, email: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Website URL" fullWidth value={schoolInfo.website} onChange={e => setSchoolInfo(p => ({ ...p, website: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Principal / Head of School" fullWidth value={schoolInfo.principal} onChange={e => setSchoolInfo(p => ({ ...p, principal: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Denomination / Affiliation</InputLabel>
                  <Select value={schoolInfo.denomination} label="Denomination / Affiliation" onChange={e => setSchoolInfo(p => ({ ...p, denomination: e.target.value }))}>
                    <MenuItem value="Non-Denominational Christian">Non-Denominational Christian</MenuItem>
                    <MenuItem value="Baptist">Baptist</MenuItem>
                    <MenuItem value="Catholic">Catholic</MenuItem>
                    <MenuItem value="Methodist">Methodist</MenuItem>
                    <MenuItem value="Presbyterian">Presbyterian</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Save Changes</Button>
            </Box>
          </Box>
        )}

        {/* Academic Settings */}
        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Academic Settings</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Current Academic Year</InputLabel>
                  <Select value={academicSettings.currentYear} label="Current Academic Year" onChange={e => setAcademicSettings(p => ({ ...p, currentYear: e.target.value }))}>
                    <MenuItem value="2023-24">2023-24</MenuItem>
                    <MenuItem value="2024-25">2024-25</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Current Term</InputLabel>
                  <Select value={academicSettings.currentTerm} label="Current Term" onChange={e => setAcademicSettings(p => ({ ...p, currentTerm: e.target.value }))}>
                    <MenuItem value="Fall 2023">Fall 2023</MenuItem>
                    <MenuItem value="Spring 2024">Spring 2024</MenuItem>
                    <MenuItem value="Summer 2024">Summer 2024</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}><TextField label="Grading Scale" fullWidth value={academicSettings.gradingScale} helperText="Define the letter grade thresholds" onChange={e => setAcademicSettings(p => ({ ...p, gradingScale: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={4}><TextField label="GPA Scale" fullWidth value={academicSettings.gpaScale} onChange={e => setAcademicSettings(p => ({ ...p, gpaScale: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Min Attendance (%)" type="number" fullWidth value={academicSettings.attendanceThreshold} helperText="Alert threshold" onChange={e => setAcademicSettings(p => ({ ...p, attendanceThreshold: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Honor Roll GPA" type="number" fullWidth value={academicSettings.honorRollGpa} onChange={e => setAcademicSettings(p => ({ ...p, honorRollGpa: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Probation GPA Threshold" type="number" fullWidth value={academicSettings.probationGpa} helperText="GPA below this triggers probation" onChange={e => setAcademicSettings(p => ({ ...p, probationGpa: e.target.value }))} /></Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Save Changes</Button>
            </Box>
          </Box>
        )}

        {/* Notifications */}
        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Admin Notification Preferences</Typography>
            {[
              { key: 'newApplication', label: 'New Application Submitted', desc: 'Receive an email when a new application is submitted' },
              { key: 'attendanceAlert', label: 'Attendance Alerts', desc: 'Alert when a student\'s attendance drops below threshold' },
              { key: 'gradeReport', label: 'Grade Report Generation', desc: 'Notify when quarterly grade reports are ready' },
              { key: 'paymentDue', label: 'Payment Reminders', desc: 'Automatic tuition payment reminders' },
              { key: 'systemAlerts', label: 'System Alerts', desc: 'System health alerts and error notifications' },
              { key: 'weeklyDigest', label: 'Weekly Summary Digest', desc: 'Receive a weekly summary of school activity every Monday' },
            ].map((pref, i) => (
              <React.Fragment key={pref.key}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{pref.label}</Typography>
                    <Typography variant="body2" color="text.secondary">{pref.desc}</Typography>
                  </Box>
                  <Switch checked={!!notifPrefs[pref.key]} onChange={e => setNotifPrefs(p => ({ ...p, [pref.key]: e.target.checked }))} color="primary" />
                </Box>
                {i < 5 && <Divider />}
              </React.Fragment>
            ))}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Save Preferences</Button>
            </Box>
          </Box>
        )}

        {/* System & Data */}
        {tab === 3 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>System Information</Typography>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    {systemInfo.map((info, i) => (
                      <Box key={info.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: i < systemInfo.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary">{info.label}</Typography>
                        {info.chip
                          ? <Chip label={info.value} color={info.color} size="small" />
                          : <Typography variant="body2" sx={{ fontWeight: 500 }}>{info.value}</Typography>}
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Backup & Maintenance</Typography>
                <Card elevation={1} sx={{ borderRadius: 2, mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Automated Backup Schedule</Typography>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Backup Frequency</InputLabel>
                      <Select defaultValue="daily" label="Backup Frequency">
                        <MenuItem value="daily">Daily at 2:00 AM</MenuItem>
                        <MenuItem value="twice">Twice Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Retention Period</InputLabel>
                      <Select defaultValue="30" label="Retention Period">
                        <MenuItem value="7">7 days</MenuItem>
                        <MenuItem value="30">30 days</MenuItem>
                        <MenuItem value="90">90 days</MenuItem>
                      </Select>
                    </FormControl>
                    <Button variant="outlined" fullWidth onClick={() => setSnackbar({ open: true, message: 'Manual backup initiated' })}>
                      Run Manual Backup Now
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Backup History</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <List dense>
                    {dataLogs.map((log, i) => (
                      <React.Fragment key={i}>
                        <ListItem>
                          <ListItemText
                            primary={log.action}
                            secondary={`${log.time} · ${log.size}`}
                          />
                          <Chip label={log.status} color="success" size="small" sx={{ mr: 1 }} />
                          <Button size="small" variant="outlined">Restore</Button>
                        </ListItem>
                        {i < dataLogs.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsAdmin;
